'use client';

import { FINANCIAL_RANGES, legacyRangesToBounds } from '@/lib/search-filters';
import { useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { Building2, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { NaceCodeItem, SimilarCompanyItem } from '@/lib/backend-api';
import { generateCompanySlug } from '@/lib/company-utils';
import { localizePathname, type Locale } from '@/lib/i18n-routes';
import { getPublicStorageUrl } from '@/lib/public-storage-url';
import { useLocale } from '@/hooks/use-locale';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LocaleLink } from '@/components/locale-link';

function logClick(button: string) {
  const body = JSON.stringify({ button });
  if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
    navigator.sendBeacon(
      '/api/log/header-click',
      new Blob([body], { type: 'application/json' }),
    );
  }
}

interface PeerResult {
  euId: string;
  name: string | null;
  registerId: string | null;
  registerType: string | null;
  registerCourt: string | null;
  city: string | null;
  industrySector: string | null;
  totalRevenue: number | null;
  faviconUrl: string | null;
}

/** Pick the bucket containing `value` plus its closest neighbour bucket. */
function closestRevenueRanges(value: number | null | undefined): string[] {
  if (value == null || !Number.isFinite(value)) return [];
  const idx = FINANCIAL_RANGES.findIndex(
    (r) => value >= (r.min ?? -Infinity) && (r.max == null || value < r.max),
  );
  if (idx === -1) return [];
  // Distance from `value` to the midpoint of a candidate bucket; the open
  // top bucket uses its lower bound as the reference point.
  const midDistance = (i: number) => {
    const r = FINANCIAL_RANGES[i];
    const mid = r.max == null ? r.min : ((r.min ?? 0) + r.max) / 2;
    return Math.abs(value - (mid ?? 0));
  };
  const prev = idx > 0 ? idx - 1 : null;
  const next = idx < FINANCIAL_RANGES.length - 1 ? idx + 1 : null;
  let neighbour: number | null = null;
  if (prev != null && next != null) {
    neighbour = midDistance(prev) <= midDistance(next) ? prev : next;
  } else if (prev != null) {
    neighbour = prev;
  } else if (next != null) {
    neighbour = next;
  }
  const ids = [FINANCIAL_RANGES[idx].id];
  if (neighbour != null) ids.push(FINANCIAL_RANGES[neighbour].id);
  return ids;
}

/** Extract the 2-digit NACE division from any NACE code (e.g. "58.2.1.2" -> "58"). */
function naceDivision(code: string | null | undefined): string | null {
  if (!code) return null;
  const m = code.replace(/\s/g, '').match(/^(\d{2})/);
  return m ? m[1] : null;
}

/** Build a locale-aware search URL filtered by NACE division + closest revenue ranges. */
function buildPeerSearchUrl(
  naceCode: string | null | undefined,
  revenue: number | null | undefined,
  locale: Locale,
): string {
  const params = new URLSearchParams();
  const division = naceDivision(naceCode);
  if (division) params.append('naceCode', division);
  const bounds = legacyRangesToBounds(closestRevenueRanges(revenue), FINANCIAL_RANGES);
  if (bounds.min !== undefined) params.set('revenueMin', String(bounds.min));
  if (bounds.max !== undefined) params.set('revenueMax', String(bounds.max));
  const qs = params.toString();
  const base = localizePathname('/search', locale);
  return qs ? `${base}?${qs}` : base;
}

function formatCompact(value: number | null | undefined): string | null {
  if (!value) return null;
  if (Math.abs(value) >= 1_000_000_000)
    return `€${(value / 1_000_000_000).toFixed(1)}B`;
  if (Math.abs(value) >= 1_000_000)
    return `€${(value / 1_000_000).toFixed(1)}M`;
  if (Math.abs(value) >= 1_000) return `€${(value / 1_000).toFixed(0)}K`;
  return `€${value.toFixed(0)}`;
}

interface CompanyPeerSearchCardProps {
  euId: string;
  revenue: number | null | undefined;
  balanceSum: number | null | undefined;
  naceCodes: NaceCodeItem[];
  /** Similarity-ranked peers shipped with the /basic payload (SSR). */
  similarCompanies: SimilarCompanyItem[];
  /** Layout classes from the page (mobile `order-*`, borders) — see MOBILE_ORDER. */
  className?: string;
}

/**
 * Presentational: the peers arrive pre-ranked in the basic response
 * (computed in the backend's fan-out, Redis-cached 7d per eu_id), so the
 * card renders server-side and its company links are crawlable — no
 * client fetch at all.
 */
export function CompanyPeerSearchCard({
  euId,
  revenue,
  balanceSum,
  naceCodes,
  similarCompanies,
  className,
}: CompanyPeerSearchCardProps) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const router = useRouter();

  // Use the most specific NACE code for the "see more" search link
  const naceEntry = useMemo(() => {
    if (!naceCodes?.length) return null;
    // Prefer the most specific code (longest/dotted), then 2-digit, then section
    const dotted = naceCodes.find((n) => n.code.includes('.'));
    if (dotted) return dotted;
    const twoDigit = naceCodes.find((n) => /^\d{2}$/.test(n.code));
    if (twoDigit) return twoDigit;
    return naceCodes[0] ?? null;
  }, [naceCodes]);

  const naceSector = naceEntry?.code ?? null;

  const referenceValue = revenue ?? balanceSum;

  const results = useMemo<PeerResult[]>(
    () =>
      (similarCompanies ?? [])
        .filter((r) => r.eu_id !== euId)
        .slice(0, 3)
        .map((r) => ({
          euId: r.eu_id,
          name: r.name ?? null,
          registerId: r.register_id ?? null,
          registerType: r.register_type ?? null,
          registerCourt: r.register_court ?? null,
          city: r.city ?? null,
          industrySector: r.industry ?? null,
          totalRevenue: r.revenue ?? null,
          faviconUrl: r.favicon_url ?? null,
        })),
    [similarCompanies, euId],
  );

  // No peers → no card at all (no empty-state placeholder).
  if (results.length === 0) return null;

  return (
    <Card variant="grid" className={className}>
      <CardHeader>
        <CardTitle as="h2">{t('companyDetail.peerSearch.title')}</CardTitle>
      </CardHeader>
      <CardContent className="px-3 pb-3 pt-0 min-h-[220px]">
        <div className="space-y-0.5">
          {results.map((item) => {
            const href = item.name
              ? generateCompanySlug(
                  {
                    name: item.name,
                    registerCourt: item.registerCourt,
                    registerType: item.registerType,
                    registerId: item.registerId,
                  },
                  locale,
                )
              : null;

            const revenueStr = formatCompact(item.totalRevenue);
            const subtitle = [item.city, item.industrySector]
              .filter(Boolean)
              .join(' · ');

            const inner = (
              <div className="flex items-center gap-3 rounded-lg px-3 py-2.5 transition-colors hover:bg-muted/60">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted/60 text-muted-foreground overflow-hidden">
                  {item.faviconUrl && getPublicStorageUrl(item.faviconUrl) ? (
                    <img
                      src={getPublicStorageUrl(item.faviconUrl)!}
                      alt={`${item.name ?? item.euId} Logo`}
                      className="h-5 w-5 object-contain"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  ) : (
                    <Building2 size={16} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate leading-tight group-hover:underline">
                    {item.name ?? item.euId}
                  </p>
                  {subtitle && (
                    <p className="text-xs text-muted-foreground truncate mt-0.5">
                      {subtitle}
                    </p>
                  )}
                </div>
                {revenueStr && (
                  <span className="text-xs font-medium text-muted-foreground shrink-0">
                    {revenueStr}
                  </span>
                )}
              </div>
            );

            return href ? (
              <LocaleLink
                key={item.euId}
                href={href}
                className="block group"
                onClick={() => logClick(`peer_company_${item.euId}`)}
              >
                {inner}
              </LocaleLink>
            ) : (
              <div key={item.euId}>{inner}</div>
            );
          })}
          {naceSector && (
            /* onClick navigation (not <a href>) so crawlers don't discover a
               unique parameterized /suche URL per company page (GSC noise) */
            <button
              type="button"
              className="flex w-full items-center justify-center gap-1 text-xs text-primary hover:underline pt-3 pb-1"
              onClick={() => {
                logClick(`peer_see_more_${naceSector}`);
                router.push(
                  buildPeerSearchUrl(
                    naceSector,
                    referenceValue,
                    locale as Locale,
                  ),
                );
              }}
            >
              {t('companyDetail.peerSearch.seeMore')}
              <ChevronRight size={14} />
            </button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
